import { defineMessages } from 'react-intl';

export const DOMAIN_NAME = 'stations' as const;

export const ReducerKeys = Object.freeze({
  STATIONS: 'stations',
  SHUFFLE_STATION: 'shuffleStation',
  CURRENT_STATION: 'currentStation',
  STATION_HISTORY_KEY: 'stationHistory',
} as const);

export const ShuffleTypes = Object.freeze({
  ALL: 'all',
  MY: 'my',
} as const);

export const ApiConstants = Object.freeze({
  // TODO: We need to implement a real paging system for MyStations
  DEFAULT_PAGE_SIZE: 250,
} as const);

export const HEADER_HEIGHT = 350 as const;
export const TITLE_CHAR_LIMIT = 64 as const;
export const DESCRIPTION_CHAR_LIMIT = 1000 as const;
export const SUGGESTIONS_TOTAL = 10 as const;
export const SEARCH_VIEWPORT_PADDING = 20 as const;
export const MAX_GET_NEXT_TRACK_RETRIES = 3 as const;
export const MAX_START_STATION_RETRIES = 3 as const;
export const MAX_RECOMMENDATIONS_TO_DISPLAY = 8 as const;

export const keys = Object.freeze({
  DETAILS: 'details',
  HAS_TAKEOVER_MODES: 'hasTakeoverModes',
  INTERACTIVE_RADIO_AVAILABLE_MODES: 'interactiveRadioAvailableModes',
  INTERACTIVE_RADIO_MODE: 'interactiveRadioMode',
  LAUNCH_ADD_VARIETY: 'launchAddVariety',
  SEEDS: 'seeds',
  STATION_ID: 'stationId',
  SUGGESTIONS: 'suggestions',
} as const);

export const DirectionKeys = Object.freeze({
  FIRST: 'first',
  NEXT: 'next',
  PREVIOUS: 'previous',
} as const);

export const actions = Object.freeze({
  ADD_SEED: 'ADD_SEED',
  DELETE_SEED: 'DELETE_SEED',
  FETCH_INTERACTIVE_RADIO_MODES: 'FETCH_INTERACTIVE_RADIO_MODES',
  GET_DETAILS: 'GET_DETAILS',
  GET_DETAILS_SUCCESS: 'GET_DETAILS_SUCCESS',
  GET_SEEDS: 'GET_SEEDS',
  GET_STATION_FEEDBACK: 'GET_STATION_FEEDBACK',
  GET_SUGGESTIONS: 'GET_SUGGESTIONS',
  HANDLE_START_STATION_ERROR: 'HANDLE_START_STATION_ERROR',
  LAUNCH_ADD_VARIETY: 'LAUNCH_ADD_VARIETY',
  LEAVE_STATION_DETAILS: 'LEAVE_STATION_DETAILS',
  LOAD_STATIONS: 'GLOBAL_LOAD_STATIONS',
  RESET_STATION_FEEDBACK_SUCCESS: 'RESET_STATION_FEEDBACK_SUCCESS',
  SET_INTERACTIVE_RADIO_MODE: 'SET_INTERACTIVE_RADIO_MODE',
  SET_SHOULD_SHORT_CIRCUIT_AFTER_GET_FRAGMENT: 'SET_SHOULD_SHORT_CIRCUIT_AFTER_GET_FRAGMENT',
  TOGGLE_EDIT_MODE: 'TOGGLE_EDIT_MODE',
} as const);

export const FilletConstants = Object.freeze({
  BIO: 'filletBio',
  EVENTS: 'filletEvents',
  LYRICS: 'filletLyrics',
  MAX_NUM_SIMILAR_ARTISTS: 6,
  SIMILAR_ARTISTS: 'filletSimilarArtists',
} as const);

export const messages = defineMessages({
  stationCreatedFrom: {
    id: 't.stationCreatedFrom',
    defaultMessage: 'Station Created From',
  },
  created: {
    id: 't.created',
    defaultMessage: 'Created: {stationInformation}',
  },
  thumbHistory: {
    id: 't.thumbHistory',
    defaultMessage: 'Thumb History',
  },
  remove: {
    id: 't.remove',
    defaultMessage: 'Remove',
  },
  artistAudioTitle: {
    id: 't.artistAudioTitle',
    defaultMessage: 'Artist Audio Messages',
  },
  artistAudioInformation: {
    id: 't.artistAudioInformation',
    defaultMessage: 'Turn on to get updates from your favorite artists.',
  },
  switchOn: {
    id: 't.switchOn',
    defaultMessage: 'On',
  },
  switchOff: {
    id: 't.switchOff',
    defaultMessage: 'Off',
  },
  defaultPlaceholderHeader: {
    id: 't.defaultPlaceholderHeader',
    defaultMessage: 'This is your space to write a description of your station.',
  },
  defaultPlaceholderWhy: {
    id: 't.defaultPlaceholderWhy',
    defaultMessage:
      'Adding a description will place this station in our search directory, and will make it available for other Pandora listeners to find.',
  },
  titleLenError: {
    id: 't.titleLenError',
    defaultMessage: 'You must enter at least one character',
  },
  titleTooLongError: {
    id: 't.titleTooLongError',
    defaultMessage: 'Your title must be less than {maxLength} characters',
  },
  saveModalConfirmation: {
    id: 't.saveModalConfirmation',
    defaultMessage: 'Changes saved!',
  },
  stationDescription: {
    id: 't.stationDescription',
    defaultMessage: 'Station Description',
  },
  stationDescriptionPlaceholder: {
    id: 't.stationDescriptionPlaceholder',
    defaultMessage: 'Add a description',
  },
  addVariety: {
    id: 't.addVariety',
    defaultMessage: 'Add Variety',
  },
  varietyModalSubtitle: {
    id: 't.varietyModalSubtitle',
    defaultMessage: 'Add Artists or Songs to this station.',
  },
  addVarietyModalPlaceholder: {
    id: 't.addVarietyModalPlaceholder',
    defaultMessage: 'Find an artist or song to add to this station.',
  },
  addVarietySuggestionTitle: {
    id: 't.addVarietySuggestionTitle',
    defaultMessage: 'Recommended',
  },
  addVarietySubtext: {
    id: 't.addVarietySubtext',
    defaultMessage: 'Artist',
  },
  cancel: {
    id: 't.cancel',
    defaultMessage: 'Cancel',
  },
  save: {
    id: 't.save',
    defaultMessage: 'Save',
  },
  searchPlaceholder: {
    id: 't.searchPlaceholder',
    defaultMessage: 'Enter an Artist, Song, or Composer',
  },
  suggestionListTitle: {
    id: 't.suggestionListTitle',
    defaultMessage: 'Stumped? Try one of these',
  },
  suggestionsListHelperText: {
    id: 't.suggestionsListHelperText',
    defaultMessage:
      'Suggestions available.  Keyboard users: use up and down arrow or tab keys to navigate and enter to select.',
  },
  resultsListHelperText: {
    id: 't.resultsListHelperText',
    defaultMessage:
      'Search results available.  Keyboard users: use up and down arrow or tab keys to navigate search results and enter to select.',
  },
  delete: {
    id: 't.delete',
    defaultMessage: 'Delete',
  },
  ok: {
    id: 't.ok',
    defaultMessage: 'OK',
  },
  viewMore: {
    id: 't.viewMore',
    defaultMessage: 'View More',
  },
  seeAll: {
    id: 't.seeAll',
    defaultMessage: 'See All',
  },
  noThumbsDown: {
    id: 't.noThumbDowns',
    defaultMessage: 'Currently no thumbs down in session history.',
  },
  noThumbsUp: {
    id: 't.noThumbDowns',
    defaultMessage: 'Currently no thumbs up in session history.',
  },
  getThumbing: {
    id: 't.getThumbing',
    defaultMessage: 'Get thumbing!',
  },
  thumbprintDescription: {
    id: 't.thumbprintDescription',
    defaultMessage: 'Music inspired by your {thumbInformation} from across all your stations.',
  },
  station: {
    id: 't.station',
    defaultMessage: 'Station',
  },
  artist: {
    id: 't.artist',
    defaultMessage: 'Artist',
  },
  songLength: {
    id: 't.songLength',
    defaultMessage: 'Song - {duration}',
  },
  deleteStationTitle: {
    id: 't.deleteStationTitle',
    defaultMessage: 'Delete Station',
  },
  removeStationTitle: {
    id: 't.removeStationTitle',
    defaultMessage: 'Remove Station',
  },
  deleteStationBody: {
    id: 't.deleteStationBody',
    defaultMessage: 'Are you sure you want to delete {stationName}?',
  },
  removeStationBody: {
    id: 't.removeStationBody',
    defaultMessage: 'Are you sure you want to remove "{stationName}" from your collection?',
  },
  lyrics: {
    id: 't.lyrics',
    defaultMessage: 'Lyrics',
  },
  seeSimilarArtists: {
    id: 't.seeSimilarArtists',
    defaultMessage: 'See Similar Artists',
  },
  outOfModeTracks: {
    id: 't.outOfModeTracks',
    defaultMessage: 'No more tracks available in this mode. Reverting to your regular station.',
  },
  discoveryTunerIntroductionText: {
    id: 't.discoveryTunerIntroductionText',
    defaultMessage: 'Tune your station',
  },
  discoveryTunerActive: {
    id: 't.discoveryTunerActive',
    defaultMessage: 'Active',
  },
});
